import { createHashHistory, createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { lazy } from 'react';
import { safeNext } from './account/access.ts';
import { currentAccess, realmsKeyOf } from './account/store.ts';
import { realms, openRealmsOf } from './db/client.ts';
import { realmFile } from './db/protocol.ts';
import { EntryPage } from './pages/EntryPage.tsx';
import { RealmLayout, RealmOverview } from './pages/RealmPage.tsx';
import { RealmsPage } from './pages/RealmsPage.tsx';
import { SearchPage } from './pages/SearchPage.tsx';
import { SharingPage } from './pages/SharingPage.tsx';
import { TablePage, TablesPage } from './pages/TablesPage.tsx';
import { ChronologyPage } from './pages/ChronologyPage.tsx';
import { CalendarPage, CalendarsPage, type CalendarSearch } from './pages/CalendarPage.tsx';
import { StoryProjectPage, StoryProjectsPage } from './pages/StoryPage.tsx';
import { ConsistencyPage } from './pages/ConsistencyPage.tsx';
import { BoardPage, BoardsPage } from './pages/BoardPage.tsx';
import { AtlasGalleryPage, AtlasMapPage } from './pages/AtlasPage.tsx';
import { PlayerViewPage } from './pages/PlayerViewPage.tsx';
import { IconLibraryPage } from './pages/IconLibraryPage.tsx';
import { KnowledgeModesPage, MODE_SLUGS, type ModeSlug } from './pages/KnowledgeModesPage.tsx';
import { CampaignPage, CampaignsPage, CAMPAIGN_TABS, type CampaignTab } from './pages/GameMasterPage.tsx';
import { SheetPage } from './pages/sheets/SheetPage.tsx';
import { BlankSheetPrintPage, SheetPrintPage } from './pages/sheets/PrintPage.tsx';
import { RealmPrintPage } from './pages/RealmPrintPage.tsx';
import { GeneratorsPage } from './pages/scripting/GeneratorsPage.tsx';
import { ScriptPage } from './pages/scripting/ScriptPage.tsx';
import { SheetsPage } from './pages/sheets/SheetsPage.tsx';
import { TemplateEditorPage } from './pages/sheets/TemplateEditor.tsx';
import type { GraphSearch } from './pages/GraphPage.tsx';
import { ErrorPanel, NotFound, Shell } from './pages/Shell.tsx';
import { WelcomePage } from './pages/WelcomePage.tsx';
import { DownloadPage } from './pages/DownloadPage.tsx';
import { SettingsPage } from './pages/SettingsPage.tsx';
import { QuickstartPage } from './help.tsx';

/** A yes-or-no search parameter: present as `true`, or `"true"` when typed into the address. */
const flag = (value: unknown) => value === true || value === 'true';

/** What an entry's address may carry after `?`. */
export interface EntrySearch {
  readonly edit?: true;
  readonly find?: string;
  readonly case?: true;
  readonly words?: true;
}

const rootRoute = createRootRoute({
  component: Shell,
  notFoundComponent: NotFound,
  errorComponent: ErrorPanel,
});

// Account pages load OPAQUE's WebAssembly and the passkey library, so they are split
// out of the main bundle and fetched only when visited.
const SignUpPage = lazy(() => import('./pages/account/SignUpPage.tsx').then((m) => ({ default: m.SignUpPage })));
const SignInPage = lazy(() => import('./pages/account/SignInPage.tsx').then((m) => ({ default: m.SignInPage })));
const RecoverPage = lazy(() => import('./pages/account/RecoverPage.tsx').then((m) => ({ default: m.RecoverPage })));
const AccountPage = lazy(() => import('./pages/account/AccountPage.tsx').then((m) => ({ default: m.AccountPage })));
const UnlockPage = lazy(() => import('./pages/account/UnlockPage.tsx').then((m) => ({ default: m.UnlockPage })));

const accountRoutes = [
  createRoute({ getParentRoute: () => rootRoute, path: '/welcome', component: WelcomePage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/signup', component: SignUpPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/signin', component: SignInPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/recover', component: RecoverPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/account', component: AccountPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/download', component: DownloadPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/quickstart', component: QuickstartPage }),
  // Outside sign-in: it never opens a realm, and draws only what the GM's window sends it.
  createRoute({ getParentRoute: () => rootRoute, path: '/player', component: PlayerViewPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/unlock',
    validateSearch: (search: Record<string, unknown>): { next?: string } => {
      const next = safeNext(search.next);
      return next ? { next } : {};
    },
    component: UnlockPage,
  }),
];

/**
 * Every realm page needs an account (docs/ACCOUNTS-PLAN.md section 5): signed in, or
 * offline as the account last signed in here (account/access.ts). Anyone else goes to
 * the welcome page. Realms are encrypted (docs/REALM-ENCRYPTION.md), so the account's
 * key must be unlocked too, or the unlock page asks for it. The check opens that
 * account's own realm storage with its key.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  beforeLoad: async ({ location }) => {
    const access = await currentAccess();
    if (access.kind !== 'allowed') throw redirect({ to: '/welcome', replace: true });
    const realmsKey = await realmsKeyOf(access.user.id);
    if (!realmsKey) {
      const next = safeNext(location.href);
      throw redirect({ to: '/unlock', search: next && next !== '/' ? { next } : {}, replace: true });
    }
    openRealmsOf(access.user.id, realmsKey);
    return { offline: access.offline };
  },
});

const realmsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  loader: () => realms.listRealms(),
  component: RealmsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  // The realms, for "Open the realm chosen below".
  loader: () => realms.listRealms(),
  component: SettingsPage,
});
const realmRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/realm/$realmKey',
  loader: ({ params }) => realms.openRealm(realmFile(params.realmKey)),
  component: RealmLayout,
  errorComponent: ErrorPanel,
});

const realmIndexRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: '/',
  component: RealmOverview,
});

// The editor (ProseMirror) is fetched only when an entry is first created or edited.
const NewEntryPage = lazy(() => import('./pages/NewEntryPage.tsx').then((m) => ({ default: m.NewEntryPage })));

const newEntryRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'new',
  component: NewEntryPage,
});

const entryRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'entry/$entryId',
  // `?edit=true` opens the entry in the editor (docs/ENTRY-EDITOR-PLAN.md section 4.5);
  // `?find=` (with `case` and `words`) highlights a search's matches (docs/SEARCH-PLAN.md section 3.5).
  validateSearch: (search: Record<string, unknown>): EntrySearch => ({
    ...(search.edit === true || search.edit === 'true' ? { edit: true as const } : {}),
    ...(typeof search.find === 'string' && search.find.trim() !== '' ? { find: search.find.slice(0, 200) } : {}),
    ...(flag(search.case) ? { case: true as const } : {}),
    ...(flag(search.words) ? { words: true as const } : {}),
  }),
  loader: ({ params }) => realms.getEntry(realmFile(params.realmKey), params.entryId),
  component: EntryPage,
  errorComponent: ErrorPanel,
});

const searchRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'search',
  validateSearch: (search: Record<string, unknown>): { q?: string; case?: true; words?: true } => ({
    ...(typeof search.q === 'string' && search.q !== '' ? { q: search.q.slice(0, 200) } : {}),
    ...(flag(search.case) ? { case: true as const } : {}),
    ...(flag(search.words) ? { words: true as const } : {}),
  }),
  component: SearchPage,
});

// The knowledge graph (docs/KNOWLEDGE-GRAPH-PLAN.md) is fetched only when first opened.
const GraphPage = lazy(() => import('./pages/GraphPage.tsx').then((m) => ({ default: m.GraphPage })));
const GRAPH_VIEWS = new Set(['all', 'factions', 'family', 'hierarchy', 'around']);
const words = (value: unknown) => (typeof value === 'string' && value.trim() !== '' ? value.trim().slice(0, 100) : undefined);

const tablesRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'tables',
  loader: ({ params }) => realms.listTables(realmFile(params.realmKey)),
  component: TablesPage,
});
const tableRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'tables/$tableId',
  loader: ({ params }) => realms.getTable(realmFile(params.realmKey), params.tableId),
  component: TablePage,
});
interface ChronologySearch {
  readonly timeline?: string;
  readonly merged?: true;
}
const chronologyRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'chronology',
  validateSearch: (search: Record<string, unknown>): ChronologySearch => {
    const timeline = words(search.timeline);
    return { ...(timeline ? { timeline } : {}), ...(timeline && flag(search.merged) ? { merged: true as const } : {}) };
  },
  loaderDeps: ({ search }) => ({ timeline: search.timeline ?? null }),
  loader: ({ params, deps }) => realms.chronology(realmFile(params.realmKey), deps.timeline),
  component: ChronologyPage,
});
const storyProjectsRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'story',
  loader: ({ params }) => realms.storyProjects(realmFile(params.realmKey)),
  component: StoryProjectsPage,
});
/** `item` is the act, chapter or scene open on the project's page. */
const storyProjectRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'story/$projectId',
  validateSearch: (search: Record<string, unknown>): { item?: string } => {
    const item = words(search.item);
    return item ? { item } : {};
  },
  loaderDeps: ({ search }) => ({ item: search.item ?? null }),
  loader: ({ params, deps }) => realms.storyProject(realmFile(params.realmKey), params.projectId, deps.item),
  component: StoryProjectPage,
});
const campaignsRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'campaigns',
  loader: ({ params }) => realms.campaigns(realmFile(params.realmKey)),
  component: CampaignsPage,
});
const campaignRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'campaigns/$campaignId',
  // `view=run` is the Run screen; otherwise Prep, on one of its tabs.
  validateSearch: (search: Record<string, unknown>): { view?: 'run'; tab?: CampaignTab } => ({
    ...(search.view === 'run' ? { view: 'run' as const } : {}),
    ...((CAMPAIGN_TABS as readonly unknown[]).includes(search.tab) && search.tab !== 'overview' ? { tab: search.tab as CampaignTab } : {}),
  }),
  loaderDeps: ({ search }) => ({ run: search.view === 'run' }),
  loader: ({ params, deps }) => realms.campaign(realmFile(params.realmKey), params.campaignId, deps.run),
  component: CampaignPage,
});
/** `mode` is which of the five; `path` the keys picked so far, column by column. */
const modesRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'modes',
  validateSearch: (search: Record<string, unknown>): { mode?: ModeSlug; path?: string } => ({
    ...((MODE_SLUGS as readonly unknown[]).includes(search.mode) ? { mode: search.mode as ModeSlug } : {}),
    ...(typeof search.path === 'string' && search.path !== '' ? { path: search.path.slice(0, 2000) } : {}),
  }),
  loader: ({ params }) => realms.knowledgeModes(realmFile(params.realmKey)),
  // The path changes as you click; the snapshot is read again only on ↻ or a new visit.
  staleTime: Infinity,
  shouldReload: false,
  component: KnowledgeModesPage,
});
const atlasRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'atlas',
  loader: ({ params }) => realms.maps(realmFile(params.realmKey)),
  component: AtlasGalleryPage,
});
const iconLibraryRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'atlas/icons',
  loader: ({ params }) => realms.icons(realmFile(params.realmKey)),
  component: IconLibraryPage,
});
/** `pin` or `region` is a place on the map to fly to and pick, from a link elsewhere. */
const atlasMapRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'atlas/$mapId',
  validateSearch: (search: Record<string, unknown>): { pin?: string; region?: string } => ({
    ...(typeof search.pin === 'string' && /^[\w-]{1,64}$/.test(search.pin) ? { pin: search.pin } : {}),
    ...(typeof search.region === 'string' && /^[\w-]{1,64}$/.test(search.region) ? { region: search.region } : {}),
  }),
  loader: ({ params }) => realms.atlasMap(realmFile(params.realmKey), params.mapId),
  component: AtlasMapPage,
});
const boardsRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'boards',
  loader: ({ params }) => realms.boards(realmFile(params.realmKey)),
  component: BoardsPage,
});
const boardRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'boards/$boardId',
  loader: ({ params }) => realms.board(realmFile(params.realmKey), params.boardId),
  component: BoardPage,
});
const consistencyRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'consistency',
  loader: ({ params }) => realms.consistency(realmFile(params.realmKey)),
  // Checked afresh each time the page opens; Check again reloads it.
  staleTime: 0,
  gcTime: 0,
  component: ConsistencyPage,
});
const calendarsRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'calendars',
  loader: ({ params }) => realms.calendars(realmFile(params.realmKey)),
  component: CalendarsPage,
});
const calendarRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'calendars/$calendarId',
  validateSearch: (search: Record<string, unknown>): CalendarSearch => (search.view === 'definition' || search.view === 'year' ? { view: search.view } : {}),
  loader: async ({ params }) => {
    const file = realmFile(params.realmKey);
    const [view, lastEventDay] = await Promise.all([realms.calendar(file, params.calendarId), realms.lastEventDay(file)]);
    return view && { ...view, lastEventDay };
  },
  component: CalendarPage,
});
const sheetsRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'sheets',
  loader: ({ params }) => realms.sheetsOverview(realmFile(params.realmKey)),
  component: SheetsPage,
});
const sheetRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'sheets/$sheetId',
  loader: ({ params }) => realms.getSheet(realmFile(params.realmKey), params.sheetId),
  component: SheetPage,
});
const sheetPrintRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'sheets/$sheetId/print',
  loader: ({ params }) => realms.getSheet(realmFile(params.realmKey), params.sheetId),
  component: SheetPrintPage,
});
const blankSheetRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'templates/$templateId/print',
  loader: ({ params }) => realms.getTemplate(realmFile(params.realmKey), params.templateId),
  component: BlankSheetPrintPage,
});
/** The realm laid out for Save as PDF (docs/PARITY-STEP-6-PLAN.md section 4). */
const realmPrintRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'print',
  loader: ({ params }) => realms.exportData(realmFile(params.realmKey)),
  component: RealmPrintPage,
});
const generatorsRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'generators',
  loader: ({ params }) => realms.scriptsOverview(realmFile(params.realmKey)),
  component: GeneratorsPage,
});
const scriptRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'scripts/$scriptId',
  loader: ({ params }) => realms.getScript(realmFile(params.realmKey), params.scriptId),
  component: ScriptPage,
});
const templateRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'templates/$templateId',
  loader: ({ params }) => realms.getTemplate(realmFile(params.realmKey), params.templateId),
  component: TemplateEditorPage,
});
const sharingRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'sharing',
  // `?sent=true`: back from a preview whose Update players' copy sent the copies.
  validateSearch: (search: Record<string, unknown>): { sent?: true } => (search.sent === true || search.sent === 'true' ? { sent: true } : {}),
  component: SharingPage,
});
const graphRoute = createRoute({
  getParentRoute: () => realmRoute,
  path: 'graph',
  validateSearch: (search: Record<string, unknown>): GraphSearch => {
    const out: Record<string, unknown> = {};
    if (typeof search.view === 'string' && GRAPH_VIEWS.has(search.view)) out.view = search.view;
    for (const key of ['rel', 'entry', 'type', 'collection'] as const) if (words(search[key])) out[key] = words(search[key]);
    if (search.steps === 2 || search.steps === '2') out.steps = 2;
    if (flag(search.list)) out.list = true;
    return out as GraphSearch;
  },
  component: GraphPage,
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    appRoute.addChildren([realmsRoute, settingsRoute, realmRoute.addChildren([realmIndexRoute, newEntryRoute, entryRoute, searchRoute, graphRoute, chronologyRoute, storyProjectsRoute, storyProjectRoute, consistencyRoute, modesRoute, atlasRoute, iconLibraryRoute, atlasMapRoute, boardsRoute, boardRoute, campaignsRoute, campaignRoute, calendarsRoute, calendarRoute, tablesRoute, tableRoute, sheetsRoute, sheetRoute, templateRoute, sheetPrintRoute, blankSheetRoute, realmPrintRoute, generatorsRoute, scriptRoute, sharingRoute])]),
    ...accountRoutes,
  ]),
  // Hash URLs work on any static host with no rewrite rules.
  history: createHashHistory(),
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
